import {
  readWorldRoomIndex,
  readWorldRoomEntry,
  type WorldRoomEntryOutcome,
  type WorldSessionClientOutcome,
} from "./worldSessionClient";
import { postLocalJson } from "./localJsonTransport";

/** Read again through the World door: a mutation acknowledgement alone cannot
 * move the rail or claim that an interrupted retirement completed. */
export async function changeWorldSessionRetirement(
  input: {
    readonly worldKey: string;
    readonly sessionId: string;
    readonly roomId?: string;
    readonly operationId: string;
    readonly fence?: number;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<WorldSessionClientOutcome | WorldRoomEntryOutcome> {
  const release = input.fence !== undefined;
  let response: Response;
  try {
    response = await postLocalJson(
      `/api/neucleos/world/sessions/${release ? "retirement/release" : "retire"}`,
      input,
      fetchImpl,
    );
  } catch {
    return {
      ok: false,
      kind: "transport",
      reason: "The retirement response was lost. Refresh to check the session before retrying.",
    };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (
    !response.ok ||
    typeof body !== "object" ||
    body === null ||
    !("ok" in body) ||
    body.ok !== true
  ) {
    const reason =
      typeof body === "object" &&
      body !== null &&
      "reason" in body &&
      typeof body.reason === "string"
        ? body.reason
        : "retirement-unresolved";
    const detail =
      typeof body === "object" &&
      body !== null &&
      "detail" in body &&
      typeof body.detail === "string" &&
      body.detail.trim()
        ? body.detail.trim().slice(0, 512)
        : undefined;
    return { ok: false, kind: reason, reason: retirementRefusalMessage(reason, detail) };
  }
  const readback =
    input.roomId === undefined
      ? await readWorldRoomIndex(input.worldKey, fetchImpl)
      : await readWorldRoomEntry(
          { worldKey: input.worldKey, roomId: input.roomId, sessionId: input.sessionId },
          fetchImpl,
        );
  if (!readback.ok) return readback;
  const row =
    "entry" in readback
      ? readback.entry.session
      : readback.index.sessions.find((session) => session.sessionId === input.sessionId);
  if (
    row === undefined ||
    (release ? row.lifecycle !== "open" || row.retirement != null : row.lifecycle !== "retired")
  )
    return {
      ok: false,
      kind: "readback-mismatch",
      reason: "The World has not confirmed this retirement change.",
    };
  return readback;
}

/** `detail` is the World's own words for a refusal it answered; a refusal
 * this app has no sentence for shows them rather than a generic line. */
export function retirementRefusalMessage(reason: string, detail?: string): string {
  switch (reason) {
    case "loopback-required":
      return "The local Cleo server rejected this app’s request. Update or restart Cleo before retrying.";
    case "execution-start-failure-unproven":
    case "world-execution-unresolved":
      return "This session has an unfinished execution. Its saved history must be reconciled before retirement.";
    case "local-execution-unresolved":
    case "pending-admission":
      return "This session still has pending or running work. Wait for it to finish before retiring.";
    case "history-on-another-device":
      return "A device has not preserved its final conversation yet. Finish that session there before retrying.";
    case "local-history-changed":
      return "The conversation changed while it was being saved. Its retirement remains pending; retry to save the new content.";
    case "unaccounted-local-history":
      return "This session has history that has not been reconciled with the World. It remains open.";
    case "world-access-refused":
      // No live sign-in holds this World on this Mac (tools themselves are
      // live, so signing in again is never how a signed-in person gets one).
      return "Cleo is not signed in to this session's World on this Mac. Sign in to Cleo in Settings → Account, then retry.";
    case "already-retired":
      return "This session has already retired. Its history remains available in the room’s memory.";
    default:
      // W-9b736ece: the World never retires a session while one under it is open.
      if (detail !== undefined && /open descendant session/i.test(detail))
        return "A session under this one is still open, and the World never retires a session before the sessions under it. Retire that one first.";
      return detail
        ? `The World did not retire this session: ${detail}`
        : "The World could not confirm retirement. The session remains available; refresh and retry, or cancel a pending retirement.";
  }
}
